import React from 'react';
import { Card, Input, Select, DatePicker } from '../UI';
import { GROUP_MODES, MORE_GROUP_MODES, filterOptions } from './erUtils';

const NAVY = '#0b318b';

// Grid items default to min-width:auto, so a control that cannot shrink below
// its intrinsic width (a native date input, a long select value) widens its
// track and pushes the whole card past the page. Wrapping each field in a
// min-width:0 cell is what lets the columns actually honour 1fr.
const Cell = ({ children }) => <div style={{ minWidth: 0 }}>{children}</div>;

const asOptions = (values, allLabel) => [
  { value: '', label: allLabel },
  ...values.map(v => ({ value: v, label: v })),
];

export default function ExpenseRegisterFilters({ allLines, filters, setFilters, groupBy, setGroupBy }) {
  const set = (key) => (value) => setFilters(f => ({ ...f, [key]: value }));
  const isMoreActive = MORE_GROUP_MODES.some(m => m.key === groupBy);

  return (
    <Card style={{
      padding: '22px 26px',
      border: '2px solid black',
      borderRadius: 16,
      boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05), 0 2px 6px -1px rgba(15, 23, 42, 0.03)',
      display: 'flex',
      flexDirection: 'column',
      gap: 18,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'grid',
        // auto-fill, not auto-fit: with an odd number of filters, auto-fit
        // collapses the empty tracks and stretches the last lone field across
        // the whole row. auto-fill keeps it the same width as its neighbours.
        gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))',
        gap: '16px 14px',
        width: '100%',
      }}>
        <Cell><Select label="Project" value={filters.project} onChange={set('project')}
          options={asOptions(filterOptions(allLines, 'project_name'), 'All')} /></Cell>
        <Cell><Select label="Vendor" value={filters.vendor} onChange={set('vendor')}
          options={asOptions(filterOptions(allLines, 'vendor_name'), 'All')} /></Cell>
        <Cell><Select label="Department" value={filters.department} onChange={set('department')}
          options={asOptions(filterOptions(allLines, 'department_name'), 'All')} /></Cell>
        <Cell><Select label="Payment Method" value={filters.paymentMethod} onChange={set('paymentMethod')}
          options={asOptions(filterOptions(allLines, 'payment_method'), 'All')} /></Cell>
        <Cell><Select label="Account Ledger" value={filters.accountLedger} onChange={set('accountLedger')}
          options={asOptions(filterOptions(allLines, 'account_ledger_name'), 'All')} /></Cell>
        <Cell><Select label="Purchase Type" value={filters.purchaseType} onChange={set('purchaseType')}
          options={asOptions(filterOptions(allLines, 'purchase_type'), 'All')} /></Cell>
        <Cell><Select label="Status" value={filters.status} onChange={set('status')}
          options={[
            { value: '', label: 'All' },
            { value: 'approved', label: 'Approved' },
            { value: 'posted', label: 'Posted' },
          ]} /></Cell>
        <Cell><DatePicker label="Date From" value={filters.dateFrom} onChange={set('dateFrom')} /></Cell>
        <Cell><DatePicker label="Date To" value={filters.dateTo} onChange={set('dateTo')} /></Cell>
      </div>

      <Input
        label="Search Keywords"
        value={filters.search}
        onChange={set('search')}
        placeholder="Search Voucher, Manual Code, Requisition, Material Code, Vendor, Waybill, BOQ, Description..."
      />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        paddingTop: 14,
        borderTop: '1px dashed var(--border-light, #cbd5e1)',
      }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          GROUP BY:
        </span>
        {GROUP_MODES.map(m => {
          const active = groupBy === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setGroupBy(m.key)}
              style={{
                padding: '8px 16px',
                background: active ? NAVY : 'var(--bg-base, #f1f5f9)',
                color: active ? '#ffffff' : 'var(--text-primary, #1e293b)',
                border: active ? `1px solid ${NAVY}` : '1px solid var(--border-light, #cbd5e1)',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 12.5,
                transition: 'all 0.18s ease',
                boxShadow: active ? '0 4px 12px rgba(11,49,139,0.22)' : 'none',
              }}
            >
              {m.label}
            </button>
          );
        })}
        <div style={{ width: 190, minWidth: 160 }}>
          <Select
            value={isMoreActive ? groupBy : ''}
            onChange={v => v && setGroupBy(v)}
            options={[{ value: '', label: 'More Groups' }, ...MORE_GROUP_MODES.map(m => ({ value: m.key, label: m.label }))]}
          />
        </div>
      </div>
    </Card>
  );
}
